import { TBufferShift, withBuffer } from "./buffer";

const pad = (value: number) => String(value).padStart(2, "0");

const HTN_LOCATION = "Hack the North";
const ICS_FOOTER =
  "Please visit my.hackthenorth.com/volunteer/shift-schedule for up-to-date information.";

/** Format a Date as UTC iCalendar DATETIME (YYYYMMDDTHHMMSSZ). */
export function toIcsUtc(date: Date): string {
  return (
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(
      date.getUTCDate()
    )}` +
    `T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(
      date.getUTCSeconds()
    )}Z`
  );
}

const escapeIcsText = (value: string) =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");

export const formatIcsLocation = (location?: string | null): string => {
  const trimmed = location?.trim();
  if (!trimmed) return HTN_LOCATION;
  return `${trimmed}, ${HTN_LOCATION}`;
};

export const formatIcsDescription = ({
  description,
  shiftLead,
  shiftType,
}: {
  description?: string | null;
  shiftLead?: string | null;
  shiftType?: string | null;
}): string => {
  const sections = [
    `Volunteer shift description:\n${description?.trim() || ""}`,
    `Shift lead:\n${shiftLead?.trim() || ""}`,
    `Shift type:\n${shiftType?.trim() || ""}`,
    ICS_FOOTER,
  ];
  return sections.join("\n\n");
};

export type TIcsShift = TBufferShift & {
  name: string;
  location?: string | null;
  description?: string | null;
  shiftLead?: string | null;
  shiftType?: string | null;
};

/**
 * Build a .ics calendar string for a volunteer's shifts using effective
 * (buffered) end times.
 */
export function buildVolunteerShiftsIcs(
  shifts: TIcsShift[],
  calendarName = "HTN Volunteer Shifts"
): string {
  const buffered = withBuffer(shifts);
  const byId = new Map(shifts.map((shift) => [shift.shiftId, shift]));
  const nowStamp = toIcsUtc(new Date());

  const events = buffered
    .map((shift) => {
      const meta = byId.get(shift.shiftId);
      if (!meta) return null;

      const description = formatIcsDescription({
        description: meta.description,
        shiftLead: meta.shiftLead,
        shiftType: meta.shiftType,
      });

      return [
        "BEGIN:VEVENT",
        `UID:volunteer-shift-${shift.shiftId}@hackthenorth.com`,
        `DTSTAMP:${nowStamp}`,
        `DTSTART:${toIcsUtc(shift.start)}`,
        `DTEND:${toIcsUtc(shift.effectiveEnd)}`,
        `SUMMARY:${escapeIcsText(`[Volunteering] ${meta.name}`)}`,
        `LOCATION:${escapeIcsText(formatIcsLocation(meta.location))}`,
        `DESCRIPTION:${escapeIcsText(description)}`,
        "END:VEVENT",
      ].join("\r\n");
    })
    .filter((event): event is string => event !== null);

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Hack the North//Volunteer Shifts//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    `X-WR-CALNAME:${escapeIcsText(calendarName)}`,
    ...events,
    "END:VCALENDAR",
  ].join("\r\n");
}

export function downloadVolunteerShiftsIcs(
  shifts: TIcsShift[],
  filename = "htn-volunteer-shifts.ics"
) {
  const ics = buildVolunteerShiftsIcs(shifts);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
